<template>
  <div class="message-input-component">
    <div class="input-container">
      <a-input
        v-model:value="messageContent"
        :placeholder="placeholder"
        :maxlength="maxLength"
        :disabled="disabled"
        @press-enter="handleSend"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
      >
        <template #suffix>
          <a-space>
            <!-- 字符计数 -->
            <span v-if="showCount" class="char-count">
              {{ messageContent.length }}/{{ maxLength }}
            </span>
            
            <!-- 发送按钮 -->
            <a-button
              type="text"
              :disabled="!canSend"
              @click="handleSend"
              :loading="sending"
              class="send-button"
            >
              <template #icon><SendOutlined /></template>
            </a-button>
          </a-space>
        </template>
      </a-input>
    </div>
    
    <!-- 输入提示和翻译信息 -->
    <div v-if="showHint" class="input-hint">
      <div class="hint-left">
        <a-typography-text type="secondary" :style="{ fontSize: '12px' }">
          按 Enter 发送，Shift + Enter 换行
        </a-typography-text>
      </div>
      <div class="hint-right">
        <a-typography-text 
          v-if="characterCount > 0" 
          type="secondary" 
          :style="{ fontSize: '12px' }"
        >
          {{ characterCount }} 字符
          <span v-if="needsTranslation" class="translation-hint">
            · 将自动翻译
          </span>
        </a-typography-text>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { SendOutlined } from '@ant-design/icons-vue'
import { message as antMessage } from 'ant-design-vue'

// Props
interface Props {
  placeholder?: string
  maxLength?: number
  disabled?: boolean
  showCount?: boolean
  showHint?: boolean
  receiverId?: number
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: '输入消息...',
  maxLength: 2000,
  disabled: false,
  showCount: true,
  showHint: true,
  receiverId: undefined
})

// Emits
const emit = defineEmits<{
  'send': [content: string]
  'typing': [typing: boolean]
  'focus': []
  'blur': []
}>()

// 注入用户信息
import { useUserStore } from '@/stores'
const userStore = useUserStore()

// 响应式数据
const messageContent = ref('')
const sending = ref(false)
const isTyping = ref(false)
const characterCount = computed(() => messageContent.value.length)

// 计算属性
const canSend = computed(() => {
  return messageContent.value.trim().length > 0 && !props.disabled && !sending.value
})

const needsTranslation = computed(() => {
  // 这里可以根据接收者的语言设置来判断是否需要翻译
  // 暂时简化处理
  return props.receiverId && characterCount.value > 0
})

// 输入处理
const handleInput = () => {
  // 发送正在输入状态
  if (!isTyping.value && messageContent.value.trim()) {
    isTyping.value = true
    emit('typing', true)
  } else if (isTyping.value && !messageContent.value.trim()) {
    isTyping.value = false
    emit('typing', false)
  }
}

// 焦点处理
const handleFocus = () => {
  emit('focus')
}

const handleBlur = () => {
  // 停止输入状态
  if (isTyping.value) {
    isTyping.value = false
    emit('typing', false)
  }
  emit('blur')
}

// 发送消息
const handleSend = async () => {
  if (!canSend.value) return
  
  const content = messageContent.value.trim()
  if (!content) return
  
  try {
    sending.value = true
    
    // 发送消息
    emit('send', content)
    
    // 清空输入框
    messageContent.value = ''
    
    // 停止输入状态
    if (isTyping.value) {
      isTyping.value = false
      emit('typing', false)
    }
  } catch (error) {
    console.error('发送消息失败:', error)
    antMessage.error('发送消息失败')
  } finally {
    sending.value = false
  }
}

// 清空输入
const clear = () => {
  messageContent.value = ''
  if (isTyping.value) {
    isTyping.value = false
    emit('typing', false)
  }
}

// 设置内容
const setContent = (content: string) => {
  messageContent.value = content
}

// 获取内容
const getContent = () => {
  return messageContent.value
}

// 监听输入内容变化，实现防抖
let typingTimer: NodeJS.Timeout | null = null
watch(messageContent, () => {
  if (typingTimer) {
    clearTimeout(typingTimer)
  }
  
  if (messageContent.value.trim()) {
    // 3秒后自动停止输入状态
    typingTimer = setTimeout(() => {
      if (isTyping.value) {
        isTyping.value = false
        emit('typing', false)
      }
    }, 3000)
  }
})

// 暴露方法
defineExpose({
  clear,
  setContent,
  getContent,
  focus: () => {
    // 可以通过ref获取input元素并聚焦
  }
})
</script>

<style lang="less" scoped>
.message-input-component {
  width: 100%;
}

.input-container {
  width: 100%;
}

.char-count {
  font-size: 12px;
  color: #999;
  user-select: none;
}

.send-button {
  color: #1890ff;
  
  &:hover:not(:disabled) {
    color: #40a9ff;
  }
  
  &:disabled {
    color: #d9d9d9;
  }
}

.input-hint {
  margin-top: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.hint-left {
  flex: 1;
}

.hint-right {
  flex-shrink: 0;
}

.translation-hint {
  color: #1890ff;
  font-weight: 500;
}

:deep(.ant-input) {
  border-radius: 20px;
  padding: 8px 16px;
}

:deep(.ant-input-suffix) {
  margin-right: 4px;
}
</style>

